// External
import type { ColumnDef } from '@tanstack/react-table';

// UI primitives
import { Badge } from '@ui/badge';

// Shared components
import { ClampedTextCell } from '@components/DataTable/ClampedTextCell';
import { DataTableColumnHeader } from '@components/DataTable/DataTableColumnHeader';
import { DataTableRowActions } from '@components/DataTable/DataTableRowActions';
import type { RowAction } from '@components/DataTable/DataTableRowActions';
import { ACTIONS_COLUMN_WIDTH } from '@components/DataTable/constants';
import { RESALE_ITEM_COLUMN_WIDTHS } from '@components/DataTable/columnWidths';
import { EmptySkuIcon } from '@icons/EmptySkuIcon';
import { EditIcon } from '@icons/EditIcon';
import { DeactivateIcon } from '@icons/DeactivateIcon';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Utilities
import { formatCurrency } from '../../utils/formatters';

// Types
import type { ResaleItem, ResaleItemStatus } from './types';
import { UNDERLINE_SLIDE_IN } from '@/components/ui/link-underline';

interface UseResaleItemColumnsOptions {
  status: ResaleItemStatus;
  onStatusToggle: (id: number, active: boolean) => void;
  onItemClick?: (id: number) => void;
  /** RBAC: when false, omit the row actions column (edit / activate / deactivate). */
  canWrite?: boolean;
}

function ActionsCell({
  resaleItem,
  isActive,
  onStatusToggle,
}: {
  resaleItem: ResaleItem;
  isActive: boolean;
  onStatusToggle: (id: number, active: boolean) => void;
}) {
  const { t } = useTranslation();

  const actions: RowAction[] = [
    {
      label: t('common.table.actions.edit'),
      icon: <EditIcon className="size-5" />,
      onClick: () => {
        window.location.href = `/resale_items/${resaleItem.id}/edit`;
      },
    },
    isActive
      ? {
          label: t('common.table.actions.deactivate'),
          icon: <DeactivateIcon className="size-5" />,
          onClick: () => onStatusToggle(resaleItem.id, false),
        }
      : {
          label: t('common.table.actions.activate'),
          icon: <CheckCircleIcon className="size-3.5" />,
          onClick: () => onStatusToggle(resaleItem.id, true),
        },
  ];

  return <DataTableRowActions actions={actions} />;
}

export function useResaleItemColumns({
  status,
  onStatusToggle,
  onItemClick,
  canWrite = true,
}: UseResaleItemColumnsOptions): ColumnDef<ResaleItem>[] {
  const { t } = useTranslation();
  const isActive = status === 'active';

  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.columns.item')} />,
      size: RESALE_ITEM_COLUMN_WIDTHS.name,
      meta: { frozen: true },
      cell: ({ row }) => {
        const image = row.original.images?.[0];
        const maxQuantity = row.original.max_quantity;
        const id = row.original.id;
        const name = row.original.name;

        const nameNode = onItemClick ? (
          <button
            type="button"
            className={`${UNDERLINE_SLIDE_IN} text-left text-sm font-semibold leading-normal text-off-black`}
            onClick={(event) => {
              event.stopPropagation();
              onItemClick(id);
            }}
          >
            {name}
          </button>
        ) : (
          <span className="text-sm font-semibold leading-normal text-off-black">
            {name}
          </span>
        );

        return (
          <div className="flex items-center gap-4">
            <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-input bg-dinnerplate-white">
              {image ? (
                <img src={image.url} alt="" className="size-full object-contain" />
              ) : (
                <EmptySkuIcon className="h-4 w-6 text-grey" />
              )}
            </div>
            <div className="flex flex-col gap-1">
              {nameNode}
              {maxQuantity != null && (
                <span className="text-xs leading-tight text-grey">
                  {t('common.columns.daily_limit')} {maxQuantity}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'vendor',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.columns.vendor')} />,
      size: RESALE_ITEM_COLUMN_WIDTHS.vendor,
      cell: ({ row }) => {
        const vendor = row.original.vendor;
        if (!vendor) return <span className="text-sm">—</span>;
        return (
          <Badge
            variant="static"
            className="w-full justify-center rounded-xs border-0 bg-dinnerplate-white px-2 py-1 text-xs font-semibold leading-tight text-grey"
          >
            {vendor}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'recommended_retail_price',
      header: t('common.columns.retail_price'),
      size: RESALE_ITEM_COLUMN_WIDTHS.retailPrice,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">{formatCurrency(row.original.recommended_retail_price)}</span>
      ),
    },
    {
      accessorKey: 'cost_to_company',
      header: t('common.columns.cost_price'),
      size: RESALE_ITEM_COLUMN_WIDTHS.costPrice,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">{formatCurrency(row.original.cost_to_company)}</span>
      ),
    },
    {
      accessorKey: 'item_notes',
      header: t('common.columns.item_notes'),
      size: RESALE_ITEM_COLUMN_WIDTHS.itemNotes,
      enableSorting: false,
      cell: ({ row }) => <ClampedTextCell text={row.original.item_notes} className="text-sm" />,
    },
    // RBAC: read-only users get no row actions column (edit / activate /
    // deactivate are all writes). The server policy enforces this regardless.
    ...(canWrite
      ? [
          {
            id: 'actions',
            enableSorting: false,
            size: ACTIONS_COLUMN_WIDTH,
            cell: ({ row }) => (
              <ActionsCell
                resaleItem={row.original}
                isActive={isActive}
                onStatusToggle={onStatusToggle}
              />
            ),
          } as ColumnDef<ResaleItem>,
        ]
      : []),
  ];
}
